import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Button } from "@heroui/react";
import { AfDataType } from "utils/TableColumnData";
import Modal from "components/Modal/Modal";

export type AfTableProps = Partial<{
  validationSchema: any;
  tableData: any[];
  setTableData: Function;
  zone: any;
  isDox: boolean;
  tableColumnData: any[];
  popupColumnData?: any[];
}>;

// Helper to get nested value from object using dot notation
const getNestedValue = (obj: any, path: string) => {
  if (!path.includes('.')) return obj[path];
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    // Parse JSON string if encountered
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (e) {
        return undefined;
      }
    }
    value = value?.[key];
  }
  return value;
};

// Helper to set nested value in object using dot notation
const setNestedValue = (obj: any, path: string, value: any) => {
  if (!path.includes('.')) {
    return { ...obj, [path]: value };
  }
  const keys = path.split('.');
  const newObj = { ...obj };
  let current = newObj;
  for (let i = 0; i < keys.length - 1; i++) {
    current[keys[i]] = current[keys[i]] ? { ...current[keys[i]] } : {};
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
  return newObj;
};

const RateAddEditSingleTable: React.FC<AfTableProps> = ({
  validationSchema,
  tableData = [],
  setTableData,
  isDox = false,
  zone = "zone a",
  tableColumnData = [],
  popupColumnData = [],
}) => {

  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const openPopup = (rowIndex: number) => {
    setSelectedRowIndex(rowIndex);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setSelectedRowIndex(null);
  };

  const handlePopupFieldChange = (key: string, value: string) => {
    if (selectedRowIndex === null) return;
    const updatedTableData = [...tableData];
    updatedTableData[selectedRowIndex] = setNestedValue(updatedTableData[selectedRowIndex], key, value);
    setTableData(updatedTableData);
  };

  // Check if pkgMaster has any values
  const hasPkgMasterData = (rowData: any) => {
    const pkgMaster = rowData?.pkgMaster;
    if (!pkgMaster) return false;
    const data = typeof pkgMaster === 'string' ? JSON.parse(pkgMaster || '{}') : pkgMaster;
    return data?.cube6 || data?.cube8 || data?.cube13;
  };

  return (
    <div className="p-5 ">
      <div className={`py-1 pr-1 rounded-md flex overflow-auto ${isDox ? "bg-black-900" : "bg-orange-600"}`}>
        <div className={`text-center px-2 w-20 min-w-20 `}>
          {isDox ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="#fff"
              className="size-10 mx-auto"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#fff" className="size-10 mx-auto">
              <path d="M3.375 3C2.339 3 1.5 3.84 1.5 4.875v.75c0 1.036.84 1.875 1.875 1.875h17.25c1.035 0 1.875-.84 1.875-1.875v-.75C22.5 3.839 21.66 3 20.625 3H3.375Z" />
              <path
                fillRule="evenodd"
                d="m3.087 9 .54 9.176A3 3 0 0 0 6.62 21h10.757a3 3 0 0 0 2.995-2.824L20.913 9H3.087Zm6.163 3.75A.75.75 0 0 1 10 12h4a.75.75 0 0 1 0 1.5h-4a.75.75 0 0 1-.75-.75Z"
                clipRule="evenodd"
              />
            </svg>
          )}
          <div className="font-semibold text-sm text-white border-b-2">
            {isDox ? <p className="text-white">DOX</p> : <p className="whitespace-nowrap">NO-DOX</p>}
          </div>

          <p className="text-white">Zone</p>
          <span className="text-white font-bold text-xl">{zone == "zone a" ? "A" : "B"}</span>
        </div>

        <table className="w-full text-sm text-left text-gray-500 border-r border-y bg-white">
          <thead className="text-xs text-gray-700 bg-gray-50 ">
            <tr>
              <th>
                <Button
                  className="mx-1 bg-gradient-to-tr from-green-500 to-green-300 text-white shadow-lg w-8 h-8 px-0 min-w-0"
                  radius="full"
                  onPress={() => {
                    setTableData((prevTableData) => [
                      ...prevTableData,
                      tableColumnData.reduce((row, column) => {
                        row[column.key] = "";
                        return row;
                      }, {}),
                    ]);
                  }}
                >
                  +
                </Button>
              </th>
              {tableColumnData.map((column) => (
                <th className="px-2 py-2 border-l border-b" key={column.key + "-head"}>
                  {column.label}
                </th>
              ))}
              {popupColumnData.length > 0 && (
                <th className="px-2 py-2 border-l border-b">Pkg Details</th>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.map((tdd, ti) => {
              return (
                <tr key={"-tr-" + ti}>
                  <td>
                    <Button
                      className="mx-1 bg-gradient-to-tr from-red-500 to-orange-600 text-white shadow-lg w-6 h-6 px-0 min-w-0"
                      radius="sm"
                      onPress={() => {
                        setTableData((prevTableData) => prevTableData.filter((_, index) => index !== ti));
                      }}
                    >
                      -
                    </Button>
                  </td>
                  {tableColumnData.map((column, i) => (
                    <td key={column.key + "-val-" + ti} className="px-1 border-l">
                      <input
                        type="text"
                        value={tdd[column.key] == null || tdd[column.key] == undefined ? "" : tdd[column.key]}
                        aria-label={column.label}
                        placeholder={column.label}
                        onChange={(e) => {
                          const updatedTableData = [...tableData];
                          updatedTableData[ti] = { ...updatedTableData[ti], [column.key]: e.target.value };
                          setTableData(updatedTableData);
                        }}
                        className=" bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-1 min-w-12"
                      />
                    </td>
                  ))}
                  {popupColumnData.length > 0 && (
                    <td className="px-1 border-l">
                      <div className="relative inline-block">
                        <Button
                          className="bg-gradient-to-tr from-blue-500 to-blue-400 text-white shadow-sm px-2 py-1 min-w-0 h-7 text-xs"
                          radius="sm"
                          onPress={() => openPopup(ti)}
                        >
                          Pkg
                        </Button>
                        {hasPkgMasterData(tdd) && (
                          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-500 rounded-full border border-white"></span>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Package Details Popup Modal */}
      <Modal isOpen={isPopupOpen} onClose={closePopup}>
        <div className="min-w-[300px]">
          <h3 className="text-lg font-semibold mb-4 text-gray-800">Package Details</h3>
          <div className="grid grid-cols-2 gap-3">
            {popupColumnData.map((column) => {
              const currentValue = selectedRowIndex !== null
                ? getNestedValue(tableData[selectedRowIndex], column.key)
                : "";
              return (
                <div key={column.key} className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    {column.label}
                  </label>
                  <input
                    type="text"
                    value={currentValue == null || currentValue == undefined ? "" : currentValue}
                    placeholder={column.label}
                    onChange={(e) => handlePopupFieldChange(column.key, e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full p-2"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RateAddEditSingleTable;
